// Move collection-filter__button-wrapper outside of shopify-section
function moveFilterButtonOutsideSection() {
  // Find the collection-filter__button-wrapper
  const filterButtonWrapper = document.querySelector('.collection-filter__button-wrapper');
  
  if (!filterButtonWrapper) return;
  
  // Find the closest shopify-section ancestor
  const shopifySection = filterButtonWrapper.closest('.shopify-section');
  
  if (!shopifySection) return;
  
  // Move the button wrapper to be a sibling of the shopify-section (after it)
  shopifySection.parentNode.insertBefore(filterButtonWrapper, shopifySection.nextSibling);

  filterButtonWrapper.style.opacity = '1';

}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', moveFilterButtonOutsideSection);
} else {
  moveFilterButtonOutsideSection();
}

class CollectionPromoHeadlineText extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.processHeadline());
    } else {
      this.processHeadline();
    }
  }

  processHeadline() {
    // Get the data-text attribute from this specific element
    const dataText = this.getAttribute('data-text');
    if (!dataText) return;

    // Find the collection-promo-headline element
    const headlineElement = document.querySelector('collection-promo-headline');
    if (!headlineElement) return;

    // Get the HTML content of this headline-text element (excluding the sr-only span)
    const spanElement = this.querySelector('span');
    if (!spanElement) return;
    
    // Create a temporary div to manipulate the HTML content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = spanElement.outerHTML;
    
    // Remove aria-hidden and sr-only class from all elements
    const elementsToClean = tempDiv.querySelectorAll('[aria-hidden], .sr-only');
    elementsToClean.forEach(element => {
      element.removeAttribute('aria-hidden');
      element.classList.remove('sr-only');
    });
    
    // Get the cleaned content - extract just the span content to avoid spacing issues
    const cleanedContent = tempDiv.innerHTML;
    
    // Get current headline content
    let headlineContent = headlineElement.innerHTML;
    
    // Find and replace the text in the headline
    if (headlineContent.includes(dataText)) {
      // Replace the text with the cleaned HTML content
      headlineContent = headlineContent.replace(dataText, cleanedContent);
      headlineElement.innerHTML = headlineContent;
    }
  }
}

// Define the custom element
customElements.define('collection-promo-headline-text', CollectionPromoHeadlineText);

// Collection Promo Headline Tile Component
class CollectionPromoHeadlineTile extends HTMLElement {
  constructor() {
    super();
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupTile());
    } else {
      this.setupTile();
    }
  }

  setupTile() {
    // Set initial styles for the tile
    this.style.position = 'absolute';
    this.style.transition = 'transform 0.1s ease-out';
    this.style.willChange = 'transform';
    
    // Set initial transform to match CSS translateY(-50%)
    this.style.transform = 'translateY(-50%)';
    
    // Initialize animation state flags
    this.mouseActive = false;
    this.parallaxActive = false;
    this.centerReached = false;
    this.floatingActive = false;
    this.isScrolling = false;
    this.isSettling = false; // New flag to prevent floating during reset
    
    // Initialize floating properties
    this.floatingOffsetX = 0;
    this.floatingOffsetY = 0;
    this.floatingSpeedX = 0.05;
    this.floatingSpeedY = 0.03;
    this.floatingDirectionX = 1;
    this.floatingDirectionY = 1;
    this.floatingRange = 8;
    
    // Add event listeners
    this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
    this.addEventListener('mousemove', this.handleMouseMove.bind(this));
    
    // Setup parallax on scroll
    this.setupParallax();
    
    // Setup floating animation
    this.setupFloating();
  }

  setupParallax() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.startParallax();
        } else {
          this.stopParallax();
        }
      });
    }, { threshold: 0.1 });

    observer.observe(this);
  }

  startParallax() {
    this.parallaxActive = true;
    this.lastScrollY = window.scrollY;
    this.parallaxOffsetY = -50; // Start with the initial -50% offset
    
    if (!this.parallaxHandler) {
      this.parallaxHandler = this.handleParallax.bind(this);
      window.addEventListener('scroll', this.parallaxHandler, { passive: true });
    }
  }

  stopParallax() {
    this.parallaxActive = false;
    if (this.parallaxHandler) {
      window.removeEventListener('scroll', this.parallaxHandler);
      this.parallaxHandler = null;
    }
  }

  handleParallax() {
    if (!this.parallaxActive) return;
    
    const currentScrollY = window.scrollY;
    const scrollDelta = currentScrollY - this.lastScrollY;
    const parallaxSpeed = 0.1;
    
    // Set scrolling flag to prevent floating conflicts
    this.isScrolling = true;
    
    // Clear scrolling flag after a short delay and reset floating position
    setTimeout(() => {
      this.isScrolling = false;
      // Reset floating to start from current position
      this.resetFloatingPosition();
    }, 300);
    
    // Accumulate parallax effect on top of initial position
    this.parallaxOffsetY += scrollDelta * parallaxSpeed;
    
    // Apply parallax effect while maintaining initial position
    // Only update if mouse is not active to avoid conflicts
    if (!this.mouseActive) {
      const rect = this.getBoundingClientRect();
      const parallaxYPixels = (this.parallaxOffsetY / 100) * rect.height;
      this.style.transform = `translate3d(0px, ${parallaxYPixels}px, 0)`;
    }
    
    this.lastScrollY = currentScrollY;
  }

  handleMouseEnter() {
    this.mouseActive = true;
    this.mouseStartX = 0;
    this.mouseStartY = 0;
    this.tileStartX = 0;
    this.tileStartY = 0;
    this.centerReached = false;
  }

  handleMouseLeave() {
    this.mouseActive = false;
    this.centerReached = false;
    
    // Add slower transition for smooth reset
    this.style.transition = 'transform 0.6s ease-out';
    
    // Reset to parallax position only (maintain initial -50% + any parallax)
    const parallaxY = this.parallaxOffsetY || -50;
    const rect = this.getBoundingClientRect();
    const parallaxYPixels = (parallaxY / 100) * rect.height;
    this.style.transform = `translate3d(0px, ${parallaxYPixels}px, 0)`;
    
    // Reset floating to start from current position
    this.resetFloatingPosition();
    
    // Reset transition back to normal after animation completes
    setTimeout(() => {
      this.style.transition = 'transform 0.1s ease-out';
    }, 600);
  }

  handleMouseMove(event) {
    if (!this.mouseActive) return;

    const rect = this.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;

    // Calculate center of the tile
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    // Calculate distance from center
    const distanceFromCenter = Math.sqrt(
      Math.pow(mouseX - centerX, 2) + Math.pow(mouseY - centerY, 2)
    );

    // Only start moving when mouse is close to center (within 100px radius)
    if (!this.centerReached && distanceFromCenter < 100) {
      this.centerReached = true;
    }

    // Don't move the tile until center is reached
    if (!this.centerReached) return;

    // Calculate mouse offset from center
    const offsetX = mouseX - centerX;
    const offsetY = mouseY - centerY;

    // Normalize the offset and apply movement limit (50px max)
    const maxMovement = 50;
    const movementX = (offsetX / centerX) * maxMovement;
    const movementY = (offsetY / centerY) * maxMovement;

    // Clamp movement to max 50px in any direction
    const clampedX = Math.max(-maxMovement, Math.min(maxMovement, movementX));
    const clampedY = Math.max(-maxMovement, Math.min(maxMovement, movementY));

    // Get current parallax offset and convert to pixels for consistent transform
    const parallaxY = this.parallaxOffsetY || -50;
    const parallaxYPixels = (parallaxY / 100) * rect.height;
    
    // Apply all transforms in one consistent format
    this.style.transform = `translate3d(${clampedX}px, ${clampedY + parallaxYPixels}px, 0)`;
  }

  setupFloating() {
    this.floatingActive = true;
    this.floatingDirectionX = Math.random() > 0.5 ? 1 : -1;
    this.floatingDirectionY = Math.random() > 0.5 ? 1 : -1;
    this.floatingSpeedX = 0.02 + Math.random() * 0.03;
    this.floatingSpeedY = 0.02 + Math.random() * 0.03;
    this.floatingRange = 24;
    this.isScrolling = false;
    this.startFloating();
  }

  startFloating() {
    if (!this.floatingActive) return;
    
    this.floatingOffsetX += this.floatingSpeedX * this.floatingDirectionX;
    this.floatingOffsetY += this.floatingSpeedY * this.floatingDirectionY;
    
    if (Math.abs(this.floatingOffsetX) > this.floatingRange) {
      this.floatingDirectionX *= -1;
      this.floatingOffsetX = Math.sign(this.floatingOffsetX) * this.floatingRange;
    }
    
    if (Math.abs(this.floatingOffsetY) > this.floatingRange) {
      this.floatingDirectionY *= -1;
      this.floatingOffsetY = Math.sign(this.floatingOffsetY) * this.floatingRange;
    }
    
    // Only apply floating when not being moused over, not actively scrolling, and not settling
    if (!this.mouseActive && !this.isScrolling && !this.isSettling) {
      const parallaxY = this.parallaxOffsetY || -50;
      const rect = this.getBoundingClientRect();
      const parallaxYPixels = (parallaxY / 100) * rect.height;
      
      // Apply floating + parallax together
      this.style.transform = `translate3d(${this.floatingOffsetX}px, ${this.floatingOffsetY + parallaxYPixels}px, 0)`;
    }
    
    requestAnimationFrame(() => this.startFloating());
  }

  resetFloatingPosition() {
    // Set settling flag to prevent floating during reset
    this.isSettling = true;
    
    // Reset floating offset to start from current visual position
    this.floatingOffsetX = 0;
    this.floatingOffsetY = 0;
    
    // Immediately update transform to current position without floating offset
    // This prevents any visual jump when resetting
    const parallaxY = this.parallaxOffsetY || -50;
    const rect = this.getBoundingClientRect();
    const parallaxYPixels = (parallaxY / 100) * rect.height;
    this.style.transform = `translate3d(0px, ${parallaxYPixels}px, 0)`;
    
    // Clear settling flag after a brief delay to allow floating to resume
    setTimeout(() => {
      this.isSettling = false;
    }, 150);
  }
}

// Define the tile custom element
customElements.define('collection-promo-headline-tile', CollectionPromoHeadlineTile);